import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { ApiError, errorMessage, get, patch, post } from '../../api.js';
import { ConfirmButton, Field, Notice, formatDateTime } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';
import { RolePicker } from './RolePicker.js';
import { StatusBadges } from './StatusBadges.js';
import type { RoleOption, UserDetail } from './types.js';

type Message = { kind: 'ok' | 'error'; text: string };

const REFUSALS: Record<string, string> = {
  self: 'You cannot deactivate your own account.',
  last_admin: 'The portal must keep at least one active administrator.',
  not_invited: 'This person has already accepted the invitation.',
};

/** One user: roles, name, and the account actions, every one audited. */
export function UserPage() {
  const { id } = useParams();
  const location = useLocation();
  const { user: me, can, refresh } = useSignedIn();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [form, setForm] = useState({ firstName: '', lastName: '', roles: [] as string[] });
  const [message, setMessage] = useState<Message | null>(
    (location.state as { notice?: Message } | null)?.notice ?? null,
  );
  const [busy, setBusy] = useState(false);
  const manage = can('admin.users.manage');

  const show = useCallback((detail: UserDetail) => {
    setUser(detail);
    setForm({ firstName: detail.firstName, lastName: detail.lastName, roles: detail.roles });
  }, []);

  useEffect(() => {
    Promise.all([get<UserDetail>(`/admin/users/${id}`), get<RoleOption[]>('/admin/roles')])
      .then(([detail, roleList]) => {
        show(detail);
        setRoles(roleList);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else
          setMessage({
            kind: 'error',
            text:
              err instanceof ApiError && err.status === 404
                ? 'This user does not exist.'
                : errorMessage(err),
          });
      });
  }, [id, show, refresh]);

  async function act(
    run: () => Promise<UserDetail | { user: UserDetail; invitationSent: boolean }>,
    done: string,
  ) {
    setBusy(true);
    setMessage(null);
    try {
      const res = await run();
      if ('invitationSent' in res) {
        show(res.user);
        setMessage(
          res.invitationSent
            ? { kind: 'ok', text: done }
            : { kind: 'error', text: 'The invitation email could not be sent. Try again later.' },
        );
      } else {
        show(res);
        setMessage({ kind: 'ok', text: done });
      }
    } catch (err) {
      const reason = err instanceof ApiError ? (err.body.reason as string | undefined) : undefined;
      setMessage({ kind: 'error', text: (reason && REFUSALS[reason]) ?? errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  if (!user) {
    return (
      <>
        <Link to="/admin/users" className="back">
          Users
        </Link>
        {message && <Notice kind={message.kind}>{message.text}</Notice>}
      </>
    );
  }

  const base = `/admin/users/${user.id}`;
  const changed =
    form.firstName.trim() !== user.firstName ||
    form.lastName.trim() !== user.lastName ||
    [...form.roles].sort().join() !== [...user.roles].sort().join();

  return (
    <>
      <Link to="/admin/users" className="back">
        Users
      </Link>
      <div className="page-head">
        <h1 className="page">
          {user.firstName} {user.lastName}
        </h1>
        <span className="badges">
          <StatusBadges user={user} />
          {user.status === 'active' && !user.locked && user.appSetUp && (
            <span className="badge b-ok">Active</span>
          )}
        </span>
      </div>
      <p className="lede">{user.email}</p>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}

      <section className="section card">
        <h2 className="sec">Sign-in</h2>
        <dl className="facts">
          <dt>Last sign-in</dt>
          <dd>{formatDateTime(user.lastSignInAt)}</dd>
          <dt>Authenticator app</dt>
          <dd>{user.appSetUp ? 'Set up' : 'Not set up yet'}</dd>
          <dt>Recovery codes left</dt>
          <dd>{user.recoveryCodesLeft}</dd>
          <dt>Trusted devices</dt>
          <dd>{user.trustedDevices}</dd>
          {user.locked && (
            <>
              <dt>Locked until</dt>
              <dd>{formatDateTime(user.lockedUntil)}</dd>
            </>
          )}
          {user.status === 'invited' && (
            <>
              <dt>Invitation valid until</dt>
              <dd>
                {user.invitationExpiresAt ? formatDateTime(user.invitationExpiresAt) : 'Expired'}
              </dd>
            </>
          )}
        </dl>
      </section>

      {manage && (
        <>
          <form
            className="section card vstack"
            onSubmit={(e) => {
              e.preventDefault();
              if (form.roles.length === 0) {
                setMessage({ kind: 'error', text: 'Choose at least one role.' });
                return;
              }
              void act(() => patch<UserDetail>(base, form), 'Changes saved.');
            }}
          >
            <h2 className="sec">Name and roles</h2>
            <div className="g2">
              <Field
                label="First name"
                required
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              />
              <Field
                label="Last name"
                required
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              />
            </div>
            <RolePicker
              roles={roles}
              value={form.roles}
              onChange={(r) => setForm({ ...form, roles: r })}
            />
            <div className="actions">
              <button type="submit" className="btn dark" disabled={busy || !changed}>
                Save changes
              </button>
            </div>
          </form>

          <section className="section card vstack">
            <h2 className="sec">Account actions</h2>
            <div className="actions">
              {user.status === 'invited' && (
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () =>
                        post<{ user: UserDetail; invitationSent: boolean }>(`${base}/invitation`),
                      `Invitation sent again to ${user.email}. The previous link no longer works.`,
                    )
                  }
                >
                  Resend the invitation
                </button>
              )}
              {user.locked && (
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() =>
                    void act(() => post<UserDetail>(`${base}/unlock`), 'Account unlocked.')
                  }
                >
                  Unlock
                </button>
              )}
              {user.appSetUp && (
                <ConfirmButton
                  label="Reset the authenticator app"
                  question="The person is signed out now, sets up the app again at the next sign-in, and is told by email."
                  disabled={busy}
                  onConfirm={() =>
                    void act(
                      () => post<UserDetail>(`${base}/reset-app`),
                      'Authenticator app reset. The person sets it up again at the next sign-in.',
                    )
                  }
                />
              )}
              {user.trustedDevices > 0 && (
                <ConfirmButton
                  label="Forget the trusted devices"
                  question="The code will be asked again on every device."
                  disabled={busy}
                  onConfirm={() =>
                    void act(
                      () => post<UserDetail>(`${base}/revoke-devices`),
                      'Trusted devices forgotten.',
                    )
                  }
                />
              )}
              {user.status === 'deactivated' ? (
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() =>
                    void act(() => post<UserDetail>(`${base}/reactivate`), 'Account reactivated.')
                  }
                >
                  Reactivate
                </button>
              ) : (
                user.id !== me.id && (
                  <ConfirmButton
                    label="Deactivate"
                    question="The person is signed out at once and can no longer sign in."
                    danger
                    disabled={busy}
                    onConfirm={() =>
                      void act(() => post<UserDetail>(`${base}/deactivate`), 'Account deactivated.')
                    }
                  />
                )
              )}
            </div>
          </section>
        </>
      )}
      {can('admin.audit.read') && (
        <p>
          <Link to={`/admin/audit?userId=${user.id}`}>See this person in the audit log</Link>
        </p>
      )}
    </>
  );
}
